import { logError } from "@/lib/log";
import { fmtMoney } from "@/lib/money";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, UploadCloud, Mail, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DRAFT_KEY = "menuone_ai_draft";

export function AiMenuGenerator() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [desc, setDesc] = useState("");
  const [mode, setMode] = useState("menu");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    const dl = searchParams.get("ai_download");
    if (!dl) return;
    if (dl === "cancel") {
      toast.info("Pagamento annullato.");
    } else {
      (async () => {
        try {
          const { data } = await api.get(`/public/ai/menu-download/status/${dl}`);
          if (data.emailed) toast.success(`Menù inviato a ${data.email}! Controlla la tua casella (anche lo spam).`);
          else toast.info("Pagamento ricevuto, invio del menù in corso…");
        } catch (e) { logError("Stato download menù AI non disponibile:", e); toast.error("Non riusciamo a verificare il pagamento. Controlla la tua email."); }
      })();
    }
    searchParams.delete("ai_download");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const generate = async () => {
    if (!desc.trim()) { toast.error("Descrivi la tua attività o i piatti da generare"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/public/ai/generate", { description: desc, mode });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const loadOnMenu = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(result));
    toast.success("Menù salvato! Registrati per pubblicarlo.");
    navigate("/register");
  };

  const dishCount = result ? result.categories.reduce((n, c) => n + (c.dishes?.length || 0), 0) : 0;

  return (
    <section id="genera-ai" className="py-24 border-t border-gray-100 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#5aa469]/10 text-[#4a8a56] text-sm font-medium px-3 py-1.5 mb-4">
            <Sparkles className="h-4 w-4" /> Provala gratis, senza registrazione
          </span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl mb-4">Genera i tuoi piatti o il tuo menù con la nostra Intelligenza Artificiale</h2>
          <p className="text-gray-500 text-lg">Descrivi la tua attività e lascia che l'AI crei categorie, piatti, descrizioni e prezzi. Poi caricalo sul tuo nuovo menù o ricevilo via email.</p>
        </div>

        <div className="rounded-3xl mo-card p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setMode("menu")} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "menu" ? "bg-[#5aa469] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="ai-mode-menu">Menù completo</button>
            <button onClick={() => setMode("dishes")} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${mode === "dishes" ? "bg-[#5aa469] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="ai-mode-dishes">Solo piatti</button>
          </div>
          <Textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={mode === "menu" ? "Es. Trattoria toscana con cucina tipica, carne alla brace e vini locali" : "Es. 5 antipasti di pesce fresco e sfizioso"}
            className="rounded-xl focus:border-[#5aa469] min-h-[90px] text-base"
            data-testid="ai-generate-input"
          />
          <Button onClick={generate} disabled={loading} className="mt-4 rounded-full h-12 px-7 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="ai-generate-btn">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Sto creando il tuo menù…</> : <><Wand2 className="h-5 w-5 mr-2" /> Genera con l'AI</>}
          </Button>

          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0 }} className="mt-8 border-t border-gray-100 pt-8" data-testid="ai-generate-result">
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
                  {result.categories.map((c, ci) => (
                    <div key={`${c.name}-${ci}`}>
                      <h3 className="font-display font-semibold text-2xl mb-1">{c.name}</h3>
                      <div className="h-0.5 w-10 bg-[#5aa469] rounded-full mb-3" />
                      <div className="space-y-2.5">
                        {(c.dishes || []).map((d, di) => (
                          <div key={`${d.name}-${di}`} className="flex justify-between gap-3">
                            <div>
                              <p className="font-medium text-gray-900">{d.name}</p>
                              {d.description && <p className="text-sm text-gray-500">{d.description}</p>}
                            </div>
                            <span className="text-[#4a8a56] font-semibold whitespace-nowrap">{fmtMoney(d.price || 0, "eur")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Button onClick={loadOnMenu} className="flex-1 rounded-full h-12 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold active:scale-95 transition-transform" data-testid="ai-load-on-menu-btn">
                    <UploadCloud className="h-5 w-5 mr-2" /> Caricalo sul tuo menù
                  </Button>
                  <Button onClick={() => setEmailOpen(true)} variant="outline" className="flex-1 rounded-full h-12 border-gray-300 bg-white hover:bg-gray-50 font-semibold" data-testid="ai-email-btn">
                    <Mail className="h-5 w-5 mr-2 text-[#5aa469]" /> Ricevilo via email · 0,99 €
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center">Caricandolo sul menù ti registri e attivi il tuo abbonamento con {dishCount} piatti già pronti.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <EmailDownloadDialog open={emailOpen} onClose={() => setEmailOpen(false)} menu={result} />
    </section>
  );
}

function EmailDownloadDialog({ open, onClose, menu }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const pay = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { toast.error("Inserisci un'email valida"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/ai/menu-download-checkout", { email: email.trim(), menu, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="ai-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Ricevi il menù via email</DialogTitle>
          <DialogDescription>Ti inviamo il menù generato all'indirizzo indicato dopo il pagamento sicuro di 0,99 € (IVA inclusa) con Stripe.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>La tua email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tua@email.it" className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" data-testid="ai-email-input" />
          </div>
          <Button onClick={pay} disabled={sending} className="w-full h-11 rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="ai-email-pay-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Paga 0,99 € e ricevi il menù"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
